import { validateShippingAddress } from "@/lib/shipping-text";
import { useSession } from "@/services/auth";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import type { QuoteRequest, QuoteResponse } from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Shared function to fetch quotes - used by both hooks
 */
async function fetchQuotes(
  token: string,
  customerId: string | undefined,
  request: QuoteRequest
): Promise<QuoteResponse> {
  validateShippingAddress(
    {
      name: request.senderName,
      line1: request.shipFrom.street1,
      line2: request.shipFrom.street2,
      city: request.shipFrom.city,
    },
    "Sender"
  );
  validateShippingAddress(
    {
      name: request.recipientName,
      line1: request.shipTo.street1,
      line2: request.shipTo.street2,
      city: request.shipTo.city,
    },
    "Recipient"
  );
  // Pricing-redesign: /get-quote replaces /get-all-options. Returns options
  // with split label/pickup prices; time slots are carried per pickup tier.
  const { data } = await axios.post<QuoteResponse>(
    `${API_URL}/api/v2/pricing/get-quote`,
    {
      customerId,

      // Pass packages with their units directly - server handles conversion
      packages: request.packages.map((pkg) => ({
        weight: pkg.weight,
        weightUnit: pkg.weightUnit,
        length: pkg.length,
        width: pkg.width,
        height: pkg.height,
        dimensionUnit: pkg.dimensionUnit,
        packageKind: pkg.packageKind ?? "PARCEL",
        description: pkg.description,
        declaredValueCents: pkg.declaredValueCents,
      })),

      // Addresses with contact info - map street1→line1, state→province
      shipFrom: {
        line1: request.shipFrom.street1,
        line2: request.shipFrom.street2 ?? undefined,
        city: request.shipFrom.city,
        province: request.shipFrom.state,
        postalCode: request.shipFrom.postalCode,
        country: request.shipFrom.country,
        name: request.senderName,
        phone: request.senderPhone ?? undefined,
        email: request.senderEmail ?? undefined,
      },
      shipTo: {
        line1: request.shipTo.street1,
        line2: request.shipTo.street2 ?? undefined,
        city: request.shipTo.city,
        province: request.shipTo.state,
        postalCode: request.shipTo.postalCode,
        country: request.shipTo.country,
        name: request.recipientName,
        phone: request.recipientPhone ?? undefined,
        email: request.recipientEmail ?? undefined,
      },
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  return data;
}

/**
 * Calculate shipping quotes with caching (useQuery)
 * Use this when you want automatic caching and refetching
 * Good for: single order dialog where component may remount
 */
export function useCalculateQuote(request: QuoteRequest | null) {
  const { token, customer } = useSession();

  // Create a stable key from request
  const requestKey = request
    ? JSON.stringify({
        senderName: request.senderName,
        recipientName: request.recipientName,
        shipFrom: request.shipFrom,
        shipTo: request.shipTo,
        packages: request.packages,
      })
    : null;

  return useQuery({
    queryKey: ["quote", requestKey],
    queryFn: async () => {
      if (!request || !token) throw new Error("No request or token");
      return fetchQuotes(token, customer?.id, request);
    },
    enabled: !!request && !!token,
    // Pricing-redesign: rates can change at any moment (admin rate-band edits,
    // FX updates, external carrier API drift). Always treat cached data as
    // stale so re-mounts refetch. The pricing step also uses `isFetching`
    // (not just `isPending`) so refetches show the loading UI instead of
    // briefly flashing the previous result.
    staleTime: 0,
    gcTime: 30 * 1000, // 30s — clear cache quickly so old options don't linger
  });
}

/**
 * Calculate shipping quotes imperatively (useMutation)
 * Use this when you need to trigger quotes manually
 * Good for: bulk operations where you call mutateAsync in a loop
 */
export function useCalculateQuoteMutation() {
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async (request: QuoteRequest) => {
      if (!token) throw new Error("No token");
      return fetchQuotes(token, customer?.id, request);
    },
  });
}
